import { useState } from 'react';

import { Row, Col, Radio, Modal, Typography, Button, Tooltip } from 'antd';

import {
  CustomNotification,
  FormAddButton,
  FormCancelButton,
  FormSaveButton,
  PreviewableModal,
  StyledModal,
} from 'common/components';

import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';

import CustomTable from 'pages/company-profile/components/tabs/contacts/components/CustomTable';
import {
  useGetCertificateForMember,
  useGetContactForMember,
  useGetLocationForMember,
} from 'pages/company-profile/components/tabs/contacts/companyContact/queries';
import { useCheckIsSuperAdmin, useGetMemberId, useGetProductFullView } from 'hooks';
import { useSafeProductIdParam } from 'hooks/useProductFullView';
import {
  InvalidateAllContactQueries,
  useGetQaSpecCertificates,
  useGetQaSpecContact,
  useGetQaSpecLocations,
  useMutationAddContactLinks,
} from './queries';
import { EmptyView } from 'pages/company-profile/components/tabs/contacts/companyContact/AssociateLocationsModal';
import { filterExistedItems, updateSelectedRowKey } from './utils';
import {
  getCertificateColumns,
  getContactColumns,
  getLocationColumns,
} from './hook';
import { checkShowPdfPreview } from 'utils/checkShowPdfPreview';
import * as ribbonActions from '@redux/ribbon/actions';
import { useDispatch } from 'react-redux';
import { openInNewTab } from 'utils/common/route';
import { useSelector } from 'react-redux';
import selectorUser from '@redux/user/selectors';
import { checkIsCompanyContactManager } from 'utils';

const { Text } = Typography;

const ASSOCIATE_TAB = {
  LOCATIONS: 'Locations',
  CONTACTS: 'Contacts',
  CERTIFICATES: 'Certificates',
};

const QASpecAssociateModal = (props) => {
  const userInfo = useSelector(selectorUser.makeSelectUserInfo());

  const isCompanyContactManager = checkIsCompanyContactManager(userInfo?.roles);
  const isSuperAdmin = useCheckIsSuperAdmin();

  const { visible, setVisible } = props;

  const [selectedRowKeys, setSelectedRowKeys] = useState({
    contactIds: [],
    locationIds: [],
    certificateIds: [],
  });
  const [associateTab, setAssociateTab] = useState(ASSOCIATE_TAB.LOCATIONS);
  const [stateActions, setStateActions] = useState({
    visible: false,
    data: null,
  });

  const hiddenFields = ['department', 'associatedRetailers'];

  const { columns: contactColumns } = getContactColumns(hiddenFields);
  const { columns: locationColumns } = getLocationColumns();
  const { columns: certificateColumns } = getCertificateColumns(
    [],
    setStateActions
  );

  const { id } = useSafeProductIdParam();
  const { productFull } = useGetProductFullView({ productId: id });
  const { memberId } = useGetMemberId();

  const { suppliers } = productFull;
  const [firstSupplier] = suppliers ?? [];

  const { locationList: locationListForMember, isLoading: locationLoading } =
    useGetLocationForMember({
      memberId: firstSupplier?.id || memberId,
    });

  const { contactList: contactListForMember, isLoading: contactLoading } =
    useGetContactForMember({
      memberId: firstSupplier?.id || memberId,
      productId: id,
    });

  const {
    certificateList: certificateListForMember,
    isLoading: certificateLoading,
  } = useGetCertificateForMember({
    memberId: firstSupplier?.id || memberId,
  });

  const { contactList: contactListForProduct } = useGetQaSpecContact({
    id,
    isEnabled: true,
  });

  const { locationList: locationListForProduct } = useGetQaSpecLocations({
    id,
    isEnabled: true,
  });
  const { certificateList: certificateListForProduct } =
    useGetQaSpecCertificates({ id, isEnabled: true });

  const { invalidateAllQueries } = InvalidateAllContactQueries(id, memberId);

  const mutation = useMutationAddContactLinks();

  const generateMessage = (response) => {
    let successMsg = '';
    const addedLocations = response.locationsAdded || 0;
    const addedContacts = response.contactsAdded || 0;
    const addedCertificates = response.certificatesAdded || 0;
    let str = '',
      pre = '',
      andCnt = 0;
    if (addedLocations) {
      str += 'Location';
      pre = ', ';
      andCnt += 1;
    }
    if (addedContacts) {
      str += pre + 'Contact';
      pre = ', ';
      andCnt += 1;
    }
    if (addedCertificates) {
      str += pre + (andCnt > 1 ? 'and ' : '') + 'Certificate';
    }

    if (str) {
      successMsg = `${str} details submitted successfully.`;
    } else {
      successMsg = 'No items were added';
    }

    return successMsg;
  };

  const handleSubmit = () => {
    try {
      const params = {
        productId: id,
        ...selectedRowKeys,
      };
      mutation.mutate(params, {
        onSuccess: (response) => {
          if (response?.isSuccess) {
            const successMsg = generateMessage(response?.data);
            CustomNotification.success(successMsg);
            invalidateAllQueries();
            setVisible(false);
          } else {
            CustomNotification.error(response?.message || 'Server Error!');
          }
        },
        onError: () => {
          CustomNotification.error('Server Error!');
        },
      });
    } catch (error) {
      CustomNotification.error('Server Error!');
    }
  };

  const onClickNext = () => {
    if (associateTab === ASSOCIATE_TAB.LOCATIONS)
      setAssociateTab(ASSOCIATE_TAB.CONTACTS);

    if (associateTab === ASSOCIATE_TAB.CONTACTS)
      setAssociateTab(ASSOCIATE_TAB.CERTIFICATES);
  };

  const onClickPrevious = () => {
    if (associateTab === ASSOCIATE_TAB.CONTACTS)
      setAssociateTab(ASSOCIATE_TAB.LOCATIONS);

    if (associateTab === ASSOCIATE_TAB.CERTIFICATES)
      setAssociateTab(ASSOCIATE_TAB.CONTACTS);
  };

  const mappingContactList = filterExistedItems(
    contactListForProduct,
    contactListForMember
  );

  const mappingLocationList = filterExistedItems(
    locationListForProduct,
    locationListForMember
  );

  const mappingCertificateList = filterExistedItems(
    certificateListForProduct,
    certificateListForMember
  ).filter((item) => item?.status == 'Active');

  const onCancelBtnClick = () => {
    setVisible(false);
  };

  const handleUpdateSelectedRowKey = (key, value) => {
    setSelectedRowKeys((prev) => {
      const selectedRowKeys = updateSelectedRowKey({
        prev,
        key,
        value,
        locationList: locationListForMember,
      });

      return selectedRowKeys;
    });
  };

  const renderTable = [
    {
      key: ASSOCIATE_TAB.CONTACTS,
      columns: contactColumns,
      dataSource: mappingContactList,
      selectedKey: 'contactIds',
      loading: contactLoading,
    },
    {
      key: ASSOCIATE_TAB.LOCATIONS,
      columns: locationColumns,
      dataSource: mappingLocationList,
      selectedKey: 'locationIds',
      loading: locationLoading,
    },
    {
      key: ASSOCIATE_TAB.CERTIFICATES,
      columns: certificateColumns,
      dataSource: mappingCertificateList,
      selectedKey: 'certificateIds',
      loading: certificateLoading,
    },
  ];

  const { data } = stateActions;

  const fileType = data?.fileExtension || data?.fileType;

  const pdfPreview =
    data?.pdfPreview ||
    data?.thumbSizeFull ||
    data?.thumbSize2500 ||
    data?.thumbSize1000 ||
    data?.thumbSize500;

  const { linkPreview, extensionFile } = checkShowPdfPreview(
    pdfPreview,
    data?.url,
    fileType
  );

  const supplierId = productFull?.suppliers?.[0]?.id ?? productFull?.ownerId ?? null;

  const dispatch = useDispatch();
  const onClickLink = () => {
    Modal.confirm({
      title: 'Confirmation',
      content: (
        <>
          <p>
            <strong>Please save your changes before proceeding</strong>. You are being redirected to the{" "}
            <strong>Member &gt; Contacts</strong> screen to add or edit details.
          </p>
          <p>
            <strong>After completing your changes, please refresh the page to see the latest updates.</strong>
          </p>
        </>
      ),
      okText: 'Ok',
      onOk: async () => {
        dispatch(ribbonActions.changeMemberLayout('contacts'));
        if (isCompanyContactManager && !isSuperAdmin) {
          openInNewTab(`/company?tab=contacts`)
        } else {
          openInNewTab(`/member-management-company/${supplierId}?tab=contacts`)
        }
      },
      width: 700
    });
  };

  return (
    <StyledModal
      title='Select Location, Contact & Certificate'
      visible={visible}
      width={1200}
      bodyStyle={{ height: 570 }}
      destroyOnClose
      maskClosable={false}
      onCancel={onCancelBtnClick}
      footer={
        <Row>
          <Col style={{ marginLeft: 'auto' }}>
            <FormCancelButton onClick={onCancelBtnClick} />
            {associateTab !== ASSOCIATE_TAB.LOCATIONS && (
              <FormAddButton
                type='primary'
                text='Previous'
                icon={<ArrowLeftOutlined />}
                onClick={onClickPrevious}
              />
            )}
            {associateTab !== ASSOCIATE_TAB.CERTIFICATES && (
              <FormAddButton
                text='Next'
                type='primary'
                icon={<ArrowRightOutlined />}
                onClick={onClickNext}
              />
            )}
            {associateTab === ASSOCIATE_TAB.CERTIFICATES && (
              <FormSaveButton
                text='Submit'
                onClick={handleSubmit}
                loading={mutation.isLoading}
              />
            )}
          </Col>
        </Row>
      }
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Radio.Group
          onChange={(e) => {
            const value = e?.target?.value;
            setAssociateTab(value);
          }}
          defaultValue={ASSOCIATE_TAB.LOCATIONS}
          buttonStyle='solid'
          value={associateTab}
          style={{ marginBottom: 8 }}
        >
          <Radio.Button value={ASSOCIATE_TAB.LOCATIONS}>
            {ASSOCIATE_TAB.LOCATIONS}
          </Radio.Button>
          <Radio.Button value={ASSOCIATE_TAB.CONTACTS}>
            {ASSOCIATE_TAB.CONTACTS}
          </Radio.Button>
          <Radio.Button value={ASSOCIATE_TAB.CERTIFICATES}>
            {ASSOCIATE_TAB.CERTIFICATES}
          </Radio.Button>
        </Radio.Group>

        {/* check permissions */}
        {(isCompanyContactManager || isSuperAdmin) &&
          (
            <Tooltip title={'Not allowed'} visible={!supplierId}>
              <Text>
                <Button
                  type="link"
                  onClick={onClickLink}
                  disabled={!supplierId}
                >
                  Manage {associateTab}
                </Button>
              </Text>
            </Tooltip>
          )}
      </div>

      {renderTable.map((table) => {
        const { key, columns, selectedKey, dataSource, loading } = table;

        if (associateTab === key) {
          let newDataSource = dataSource;
          if (key === 'Locations') {
            newDataSource = newDataSource?.map((item) => ({
              ...item,
              type: Array.isArray(item?.type) ? item?.type?.join(', ') : null,
            }));
          }

          return (
            <div style={{ height: 500 }}>
              {dataSource?.length > 0 || loading ? (
                <CustomTable
                  key={key}
                  loading={loading}
                  columns={columns}
                  dataSource={newDataSource}
                  selectedRowKeys={selectedRowKeys[selectedKey]}
                  setSelectedRowKeys={(value) => {
                    handleUpdateSelectedRowKey(selectedKey, value);
                  }}
                  isMultipleSelect
                />
              ) : (
                <EmptyView text={`No ${key} available to link`} />
              )}
              {stateActions.visible && (
                <PreviewableModal
                  id={data?.id}
                  link={linkPreview}
                  title={data?.certificateName}
                  visible={stateActions.visible}
                  fileName={data?.certificateName}
                  cancelModal={() =>
                    setStateActions({ visible: false, data: null })
                  }
                  isDownload={false}
                  fileType={extensionFile}
                  assetData={data}
                  isDocument={true}
                />
              )}
            </div>
          );
        } else return null;
      })}
    </StyledModal>
  );
};

export default QASpecAssociateModal;
